import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControlLabel,
  Switch,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import PortalLayout from '../../components/PortalLayout';
import { useToast } from '../../context/ToastContext';

interface ErrorPageConfig {
  title: string;
  subtitle: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  backgroundColor?: string;
  enabled: boolean;
}

interface ErrorPagesState {
  '404': ErrorPageConfig;
  '500': ErrorPageConfig;
  '403': ErrorPageConfig;
}

const ErrorPageEditorPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [selectedPage, setSelectedPage] = useState<'404' | '500' | '403'>('404');
  const [showPreview, setShowPreview] = useState(false);

  const [configs, setConfigs] = useState<ErrorPagesState>({
    '404': {
      title: 'Seite nicht gefunden',
      subtitle: '404',
      description: 'Die angeforderte Seite existiert nicht oder wurde verschoben.\nBitte überprüfen Sie die URL und versuchen Sie es erneut.',
      primaryButtonText: 'Zur Startseite',
      secondaryButtonText: 'Zurück',
      backgroundColor: '#667eea',
      enabled: true,
    },
    '500': {
      title: 'Interner Serverfehler',
      subtitle: '500',
      description: 'Ein unerwarteter Fehler ist auf dem Server aufgetreten.\nUnser Team wurde benachrichtigt und arbeitet an der Lösung.',
      primaryButtonText: 'Seite neu laden',
      secondaryButtonText: 'Zur Startseite',
      backgroundColor: '#f093fb',
      enabled: true,
    },
    '403': {
      title: 'Zugriff verweigert',
      subtitle: '403',
      description: 'Sie haben keine Berechtigung, diese Seite zu öffnen.\nKontaktieren Sie den Administrator für weitere Informationen.',
      primaryButtonText: 'Zur Startseite',
      secondaryButtonText: 'Zurück',
      backgroundColor: '#ffa726',
      enabled: true,
    },
  });

  const currentConfig = configs[selectedPage];

  const handleConfigChange = (field: keyof ErrorPageConfig, value: any) => {
    setConfigs({
      ...configs,
      [selectedPage]: {
        ...currentConfig,
        [field]: value,
      },
    });
  };

  const handleSave = async () => {
    try {
      console.log('Saving error page config:', configs[selectedPage]);
      showSuccess(`Error-Seite ${selectedPage} erfolgreich gespeichert`);
    } catch (err) {
      showError('Fehler beim Speichern der Error-Seite');
    }
  };

  const handleReset = () => {
    const defaults: ErrorPagesState = {
      '404': {
        title: 'Seite nicht gefunden',
        subtitle: '404',
        description: 'Die angeforderte Seite existiert nicht oder wurde verschoben.',
        primaryButtonText: 'Zur Startseite',
        secondaryButtonText: 'Zurück',
        backgroundColor: '#667eea',
        enabled: true,
      },
      '500': {
        title: 'Interner Serverfehler',
        subtitle: '500',
        description: 'Ein unerwarteter Fehler ist auf dem Server aufgetreten.',
        primaryButtonText: 'Seite neu laden',
        secondaryButtonText: 'Zur Startseite',
        backgroundColor: '#f093fb',
        enabled: true,
      },
      '403': {
        title: 'Zugriff verweigert',
        subtitle: '403',
        description: 'Sie haben keine Berechtigung, diese Seite zu öffnen.',
        primaryButtonText: 'Zur Startseite',
        secondaryButtonText: 'Zurück',
        backgroundColor: '#ffa726',
        enabled: true,
      },
    };
    setConfigs(defaults);
    showSuccess('Error-Seiten auf Standardeinstellungen zurückgesetzt');
  };

  return (
    <PortalLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 4 }}>
          Error-Seiten Editor
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: { xs: 1, md: 0.25 } }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Error-Seiten
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(['404', '500', '403'] as const).map((code) => (
                  <Button
                    key={code}
                    variant={selectedPage === code ? 'contained' : 'outlined'}
                    onClick={() => setSelectedPage(code)}
                    sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                  >
                    {code}: {configs[code].title}
                  </Button>
                ))}
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: { xs: 1, md: 0.75 } }}>
            {showPreview ? (
              <Card>
                <CardHeader
                  title="Vorschau"
                  action={
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={() => setShowPreview(false)}
                    >
                      Bearbeiten
                    </Button>
                  }
                />
                <CardContent>
                  <Box
                    sx={{
                      minHeight: '500px',
                      background: `linear-gradient(135deg, ${currentConfig.backgroundColor} 0%, ${currentConfig.backgroundColor}dd 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 2,
                      p: 4,
                    }}
                  >
                    <Paper sx={{ p: 4, textAlign: 'center', maxWidth: '500px' }}>
                      <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2, color: currentConfig.backgroundColor }}>
                        {currentConfig.subtitle}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {currentConfig.title}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                        {currentConfig.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button variant="contained" sx={{ background: currentConfig.backgroundColor }}>
                          {currentConfig.primaryButtonText}
                        </Button>
                        {currentConfig.secondaryButtonText && (
                          <Button variant="outlined" sx={{ color: currentConfig.backgroundColor }}>
                            {currentConfig.secondaryButtonText}
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader
                  title={`Bearbeiten: ${selectedPage} - ${currentConfig.title}`}
                  action={
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={() => setShowPreview(true)}
                    >
                      Vorschau
                    </Button>
                  }
                />
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentConfig.enabled}
                        onChange={(e) =>
                          handleConfigChange('enabled', e.target.checked)
                        }
                      />
                    }
                    label="Error-Seite aktiviert"
                  />

                  <TextField
                    label="Untertitel (Error-Code)"
                    value={currentConfig.subtitle}
                    onChange={(e) =>
                      handleConfigChange('subtitle', e.target.value)
                    }
                    fullWidth
                  />

                  <TextField
                    label="Titel"
                    value={currentConfig.title}
                    onChange={(e) => handleConfigChange('title', e.target.value)}
                    fullWidth
                  />

                  <TextField
                    label="Beschreibung"
                    value={currentConfig.description}
                    onChange={(e) =>
                      handleConfigChange('description', e.target.value)
                    }
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Unterstützt Zeilenumbrüche"
                  />

                  <TextField
                    label="Primärer Button-Text"
                    value={currentConfig.primaryButtonText}
                    onChange={(e) =>
                      handleConfigChange('primaryButtonText', e.target.value)
                    }
                    fullWidth
                  />

                  <TextField
                    label="Sekundärer Button-Text (optional)"
                    value={currentConfig.secondaryButtonText || ''}
                    onChange={(e) =>
                      handleConfigChange('secondaryButtonText', e.target.value)
                    }
                    fullWidth
                  />

                  <TextField
                    label="Hintergrundfarbe"
                    type="color"
                    value={currentConfig.backgroundColor || '#667eea'}
                    onChange={(e) =>
                      handleConfigChange('backgroundColor', e.target.value)
                    }
                    fullWidth
                  />

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                    >
                      Speichern
                    </Button>
                    <Button variant="outlined" onClick={handleReset}>
                      Zurücksetzen
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Container>
    </PortalLayout>
  );
};

export default ErrorPageEditorPage;
